import { Flex } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import WidgetCard from '../common/WidgetCard';
import ProgressWidget, { ProgressWidgetSkeleton } from './ProgressWidget';
import StatisticsRadarChart from './StatisticsRadarChart';

import useTeamProgressQuery from 'screens/season-overview/hooks/useTeamProgressQuery';

import { selectToken } from 'store/auth/selectors';
import { ROLE_GROUPS } from 'utils/constants';
import {
  calcPercentage,
  formatCurrencyShortened,
  formatNumberWithComma,
  isAuthorizedToAccessResource,
} from 'utils/helpers';

import type { SeasonOverviewCommonWidgetFilters } from 'screens/season-overview/types';

type MetricType =
  | 'sales'
  | 'hectares'
  | 'seasonTasks'
  | 'collection'
  | 'deliveries';

interface MetricConfig {
  type: MetricType;
  labelKey: string;
  formatValue: (achieved: number, target: number, country?: string) => string;
}

const TeamProgressWidget = ({
  country,
  seasonTypeId,
}: SeasonOverviewCommonWidgetFilters) => {
  const { t } = useTranslation();
  const token = useSelector(selectToken);

  const canAccessTeamProgress = useMemo(
    () => isAuthorizedToAccessResource(token, ROLE_GROUPS.ALL_EXCEPT_JR_AG),
    [token],
  );

  const params = useMemo(
    () => ({ country, seasonTypeId, enabled: canAccessTeamProgress }),
    [seasonTypeId, country, canAccessTeamProgress],
  );
  const { isLoading, data: teamProgressData } = useTeamProgressQuery(params);

  const metricConfigs: MetricConfig[] = useMemo(
    () => [
      {
        type: 'sales',
        labelKey: 'seasonOverview.table.salesTargets',
        formatValue: (achieved, target, country) => {
          const currency = country === 'Indonesia' ? 'Rp' : '₫';
          return `${currency} ${formatCurrencyShortened(achieved)}/${currency} ${formatCurrencyShortened(target)}`;
        },
      },
      {
        type: 'hectares',
        labelKey: 'seasonOverview.table.hectareTarget',
        formatValue: (achieved, target) =>
          `${formatNumberWithComma(achieved)}/${formatNumberWithComma(target)} HA`,
      },
      {
        type: 'seasonTasks',
        labelKey: 'seasonOverview.table.seasonTasks',
        formatValue: (achieved, target) =>
          `${formatNumberWithComma(achieved)}/${formatNumberWithComma(target)} Tasks`,
      },
      {
        type: 'collection',
        labelKey: 'seasonOverview.table.collections',
        formatValue: (achieved, target, country) => {
          const currency = country === 'Indonesia' ? 'Rp' : '₫';
          return `${currency} ${formatCurrencyShortened(achieved)}/${currency} ${formatCurrencyShortened(target)}`;
        },
      },
      {
        type: 'deliveries',
        labelKey: 'seasonOverview.table.deliveries',
        formatValue: (achieved, target) =>
          `${formatNumberWithComma(achieved)}/${formatNumberWithComma(target)} Packages`,
      },
    ],
    [],
  );

  const metrics = useMemo(() => {
    return metricConfigs.reduce(
      (acc, config) => {
        const achieved = teamProgressData?.[config.type]?.actualTarget || 0;
        const target = teamProgressData?.[config.type]?.totalTarget || 0;
        const label = t(config.labelKey) || '';

        acc[label] = {
          metric: config.formatValue(achieved, target, country as string),
          progress: +calcPercentage(achieved, target),
        };

        return acc;
      },
      {} as Record<string, { metric: string; progress: number }>,
    );
  }, [teamProgressData, country, t, metricConfigs]);

  const chartData = useMemo(() => {
    return Object.entries(metrics).map(([key, value]) => ({
      item: key,
      value: Math.min(value.progress, 100),
    }));
  }, [metrics]);

  if (!canAccessTeamProgress) return null;

  return (
    <WidgetCard
      header={t('seasonOverview.widgets.teamProgress.header')}
      className="team-progress"
    >
      <Flex wrap="wrap" align="flex-start" gap={40}>
        <div className="team-progress__widget-container">
          {isLoading ? (
            <>
              {metricConfigs.map((_, index) => (
                <ProgressWidgetSkeleton key={index} />
              ))}
            </>
          ) : (
            <>
              {metricConfigs.map((config) => {
                const label = t(config.labelKey) || '';
                return (
                  <ProgressWidget
                    key={label}
                    header={label}
                    progressPercent={metrics[label]?.progress || 0}
                    metric={metrics[label]?.metric || ''}
                  />
                );
              })}
            </>
          )}
        </div>

        <Flex
          align="center"
          justify="center"
          className="team-progress__chart-container"
        >
          <StatisticsRadarChart data={chartData} />
        </Flex>
      </Flex>
    </WidgetCard>
  );
};

export default TeamProgressWidget;
